import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { postReview, verifyRecaptchaToken } from "../../Providers/reviewsProvider";
import icons from "../../icons.config";
import { MdStar, MdStarOutline } from "react-icons/md";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from "react-google-recaptcha-v3";

// ReviewModal component with reCAPTCHA and success popup
const ReviewModal = ({ isOpen, onClose, productId, sku }) => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState("");
  const [location, setLocation] = useState("");
  const [images, setImages] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recaptchaError, setRecaptchaError] = useState("");
  const [showSuccessPopup, setShowSuccessPopup] = useState(false); // State for success popup
  const authToken = localStorage.getItem("authToken");

  const { executeRecaptcha } = useGoogleReCaptcha();

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (images.length + files.length <= 5) {
      setImages([...images, ...files]);
    } else {
      alert("You can upload a maximum of 5 images.");
    }
  };

  const removeImage = (index) => {
    setImages(images.filter((_, i) => i !== index));
  };

  const showTemporaryError = (setter, message) => {
    setter(message);
    setTimeout(() => setter(""), 3000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!rating) {
      alert("Please provide a rating.");
      return;
    }
    if (!comment) {
      alert("Please provide a comment.");
      return;
    }
    if (!location) {
      alert("Please provide your location.");
      return;
    }

    if (!executeRecaptcha) {
      console.error("reCAPTCHA not available");
      showTemporaryError(setRecaptchaError, "reCAPTCHA not loaded. Please try again.");
      return;
    }

    setIsSubmitting(true);
    try {
      console.log("Executing reCAPTCHA from google.com...");
      const token = await executeRecaptcha("submit_review");
      console.log("reCAPTCHA Token:", token);

      if (!token) {
        console.error("reCAPTCHA failed to generate token");
        showTemporaryError(setRecaptchaError, "reCAPTCHA verification failed. Please try again.");
        setIsSubmitting(false);
        return;
      }

      // Verify token separately via custom API
      await verifyRecaptchaToken(token);

      // Submit review data without token
      const formData = new FormData();
      formData.append("productId", productId);
      formData.append("sku", sku);
      formData.append("rating", rating);
      formData.append("comment", comment);
      formData.append("location", location);
      images.forEach((image) => formData.append("images", image));

      await postReview(formData);
      setShowSuccessPopup(true); // Show success popup
      setTimeout(() => {
        setShowSuccessPopup(false); // Hide popup after 3 seconds
        onClose(); // Close modal after popup hides
      }, 3000);
      setRating(0);
      setComment("");
      setLocation("");
      setImages([]);
    } catch (error) {
      console.error("Failed to submit review:", error);
      showTemporaryError(setRecaptchaError, error.message || "Failed to submit review. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderStarsInput = () => {
    return (
      <div className="flex space-x-1">
        {[...Array(5)].map((_, index) => {
          const starValue = index + 1;
          const isFilled = starValue <= (hoverRating || rating);
          return (
            <button
              key={index}
              type="button"
              onClick={() => setRating(starValue)}
              onMouseEnter={() => setHoverRating(starValue)}
              onMouseLeave={() => setHoverRating(0)}
              className={`text-2xl cursor-pointer ${isFilled ? "text-review" : "text-yellow-400"}`}
            >
              {isFilled ? <MdStar /> : <MdStarOutline />}
            </button>
          );
        })}
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="bg-tertiary p-6 rounded-lg w-full max-w-md relative px-12 md:px-5"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-2 right-2 text-gray-600"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        <h2 className="text-2xl font-light font-outfit mb-4">Add Review</h2>
        <form onSubmit={handleSubmit}>
          <div className="flex text-xl mb-4 justify-start">Rating: {renderStarsInput()}</div>
          <input
            type="text"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="Your Location"
            className="w-full p-2 mb-3 border border-primary1 focus:border-primary1 focus:border-2 focus:outline-none text-hcolor rounded"
            required
          />
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Your Comment"
            className="w-full p-2 mb-3 border border-primary1 focus:border-primary1 focus:border-2 focus:outline-none text-hcolor rounded"
            rows="4"
            required
          />
          <div className="mb-3">
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleImageChange}
              className="w-full p-2 border border-primary1 focus:border-primary1 focus:border-2 focus:outline-none rounded"
            />
            <div className="flex flex-wrap gap-2 mt-2">
              {images.map((image, index) => (
                <div key={index} className="relative">
                  <img
                    src={URL.createObjectURL(image)}
                    alt={`Preview ${index}`}
                    className="w-16 h-16 object-cover"
                  />
                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    className="absolute top-0 right-0 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
            <p className="text-sm text-gray-500">
              {images.length}/5 images uploaded
            </p>
          </div>
          {recaptchaError && <p className="text-red-500 text-sm mb-3">{recaptchaError}</p>}
          <div className="flex justify-end gap-3">
            <button
              type="submit"
              className="px-4 py-2 bg-hcolor text-tertiary rounded hover:bg-tertiary hover:text-hcolor border-2 border-hcolor"
              disabled={isSubmitting}
            >
              Add Review
            </button>
          </div>
        </form>
      </div>

      {/* Success Popup */}
      <AnimatePresence>
        {showSuccessPopup && (
          <motion.div
            className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-60"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.3 }}
          >
            Review added successfully
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Wrap ReviewsSection with GoogleReCaptchaProvider
function ReviewsSectionWithProvider(props) {
  return (
    <GoogleReCaptchaProvider
      reCaptchaKey="6LfeWBIrAAAAALNssjtaKBGyP0XCFZrLD2uxGNZi" // Replace with your real v3 site key
      useRecaptchaNet={false} // Ensures google.com
    >
      <ReviewsSection {...props} />
    </GoogleReCaptchaProvider>
  );
}

const ReviewsSection = ({
  isMobile,
  reviews,
  currentReviewIndex,
  handlePreviousReview,
  handleNextReview,
  renderStars,
  setCurrentReviewIndex,
  productId,
  sku,
  isAuthenticated,
  setShowLoginPrompt,
}) => {
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);

  const handleAddReviewClick = () => {
    if (isAuthenticated) {
      setIsReviewModalOpen(true);
    } else {
      setShowLoginPrompt(true);
    }
  };

  return (
    <>
      <div className="hidden lg:block relative w-full max-w-3xl py-6">
        <h1 className="text-primary1 flex justify-center text-xl font-outfit font-light">REVIEWS</h1>
        <img
          src={icons.arrowLeft}
          alt="Previous Review"
          className="absolute left-0 top-[50%] transform -translate-y-1/2 w-8 h-8 rounded-full bg-secondary z-20 hidden lg:block cursor-pointer"
          onClick={handlePreviousReview}
        />
        <div className="bg-gray-100 p-3 rounded-lg text-center">
          {reviews.length > 0 && reviews[currentReviewIndex] && (
            <AnimatePresence mode="wait">
              <motion.div
                key={currentReviewIndex}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.5 }}
              >
                <div className="flex justify-center mb-2">{renderStars(reviews[currentReviewIndex].rating)}</div>
                <div className="text-center mb-2">
                  <p className="font-outfit font-normal text-2xl text-primary1">{reviews[currentReviewIndex].name}</p>
                  <p className="text-sm font-outfit text-hcolor font-light">{reviews[currentReviewIndex].city}</p>
                </div>
                <p className="text-sm text-hcolor text-center font-outfit font-light">"{reviews[currentReviewIndex].review}"</p>
              </motion.div>
            </AnimatePresence>
          )}
        </div>
        <img
          src={icons.arrowRight}
          alt="Next Review"
          className="absolute right-0 top-[50%] transform -translate-y-1/2 w-8 h-8 rounded-full bg-secondary z-20 hidden lg:block cursor-pointer"
          onClick={handleNextReview}
        />
        <div className="flex justify-center mt-4">
          <button
            onClick={handleAddReviewClick}
            className="px-4 py-2 bg-hcolor text-tertiary hover:bg-tertiary hover:text-hcolor rounded border-2 border-hcolor"
          >
            Add Your Review
          </button>
        </div>
      </div>
      <div className="lg:hidden relative w-full max-w-3xl mx-auto">
        <h1 className="text-primary1 flex justify-center text-2xl font-outfit font-light">REVIEWS</h1>
        <div className="bg-gray-100 p-3 rounded-lg text-center">
          {reviews.length > 0 && currentReviewIndex < reviews.length ? (
            <AnimatePresence mode="wait">
              <motion.div
                key={currentReviewIndex}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.5 }}
              >
                <div className="flex justify-center mb-2">{renderStars(reviews[currentReviewIndex]?.rating)}</div>
                <div className="text-center mb-2">
                  <p className="font-outfit font-normal text-2xl text-primary1">{reviews[currentReviewIndex]?.name}</p>
                  <p className="text-sm font-outfit text-hcolor font-light">{reviews[currentReviewIndex]?.city}</p>
                </div>
                <p className="text-sm text-hcolor text-center font-outfit font-light">"{reviews[currentReviewIndex]?.review}"</p>
              </motion.div>
            </AnimatePresence>
          ) : (
            <p className="text-gray-500 text-sm font-outfit">No reviews have been added for this dress.</p>
          )}
        </div>
        <div className="flex justify-center z-10 relative top-5">
          {Array.from({ length: reviews.length }).map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentReviewIndex(index)}
              className={`w-2 h-2 md:h-3 md:w-3 mx-2 rounded-full ${
                index === currentReviewIndex ? "bg-hcolor" : "bg-white border border-hcolor"
              } shadow-lg`}
            />
          ))}
        </div>
        <div className="flex justify-center mt-8">
          <button
            onClick={handleAddReviewClick}
            className="px-4 py-2 bg-hcolor text-tertiary hover:bg-tertiary hover:text-hcolor rounded border-2 border-hcolor"
          >
            Add Your Review
          </button>
        </div>
      </div>
      <ReviewModal
        isOpen={isReviewModalOpen}
        onClose={() => setIsReviewModalOpen(false)}
        productId={productId}
        sku={sku}
      />
    </>
  );
};

export default ReviewsSectionWithProvider;